import React, { Fragment, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tab } from "@headlessui/react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import { currencyType, getCarrierLogo } from "../../../utils/flightInfo";
import convertNumbThousand from "../../../utils/convertNumbThousand";
import NcImage from "../../NcImage/NcImage";
import ButtonPrimary from "../../Button/ButtonPrimary";
import customTime from "../../../utils/customTime";
import { AmadeusTicketInfo } from "../Ticket/TicketInfo.amadeus";
// import { AirArabiaTicketInfo } from "../Ticket/TicketInfo.airarabia";

const ConfirmTicketAmadeus = ({ className = "" }) => {
  const location = useLocation();

  const { ticket, charges, serviceCharge, currency } = location.state;

  const travelers = ticket?.travelers;
  const offer = ticket?.flightOffers?.[0];

  const componentRef = useRef(null);

  function totalPrice() {
    let totalPrice = offer?.price?.total;
    totalPrice = parseFloat(totalPrice);

    let result =
      totalPrice + parseFloat(charges || 0) + parseFloat(serviceCharge || 0);
    return convertNumbThousand(result);
  }

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const renderContent = () => {
    return (
      <div className="flex flex-col p-6 px-4 space-y-8 w-full shadow sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 sm:p-6 xl:p-8">
        <h2 className="text-3xl font-semibold lg:text-4xl">
          Congratulation 🎉
        </h2>
        <p className="text-lg font-semibold text-green-600">
          Your booking has been confirmed successfully!
        </p>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>

        <div className="mb-4 space-y-6">
          <h3 className="text-2xl font-semibold">Your flight</h3>
          <div className="flex flex-col p-6 mb-5 space-y-6 w-full rounded shadow sm:rounded-2xl lg:border border-neutral-200 dark:border-neutral-700 sm:space-y-8 sm:p-6 xl:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center">
              <div className="flex-shrink-0 w-full sm:w-40">
                <NcImage
                  src={getCarrierLogo(offer?.validatingAirlineCodes?.[0])}
                />
              </div>
              <div className="py-5 space-y-3 sm:px-5">
                <span className="text-lg font-semibold text-neutral-500">
                  <h1>{offer?.validatingAirlineCodes?.[0]}</h1>
                </span>

                {offer?.itineraries?.map((itinerary, itineraryIndex) => (
                  <div key={itineraryIndex}>
                    {itinerary.segments?.map((segment, segmentIndex) => (
                      <div key={segmentIndex}>
                        <span className="text-lg font-semibold text-neutral-500 line-clamp-1">
                          <h1>
                            {segment.departure?.iataCode} -{" "}
                            {segment.arrival?.iataCode}
                          </h1>
                        </span>
                        <span className="block mt-1 text-base font-medium">
                          {moment(segment.departure?.at).format("DD MMM YYYY")}{" "}
                          - {moment(segment.arrival?.at).format("DD MMM YYYY")}
                        </span>
                        <span className="block mt-1 text-base font-medium">
                          {customTime(segment.departure?.at)} -{" "}
                          {customTime(segment.arrival?.at)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}

                <div className="w-10 border-b border-neutral-200 dark:border-neutral-700"></div>
              </div>
            </div>
            <div className="flex flex-col space-y-4">
              <h3 className="text-2xl font-semibold">Price detail</h3>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span>Price</span>
                <span>
                  {offer?.price?.currency}{" "}
                  {convertNumbThousand(+offer?.price?.total + (charges || 0))}
                </span>
              </div>

              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span>Service charge</span>
                <span>
                  {charges !== 0 ? charges : serviceCharge}{" "}
                  {currencyType(currency)}
                </span>
              </div>
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span>Agent Fee</span>
                <span>
                  {ticket?.commissions?.[0]?.values?.[0]?.amount || 0}{" "}
                  {currencyType(currency)}
                </span>
              </div>
              <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
              <div className="flex justify-between text-xl font-bold text-black">
                <span>Total</span>
                <span>
                  {offer?.price?.currency} {totalPrice()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-2xl font-semibold">Flight detail</h3>
          <div className="flex flex-col space-y-4">
            <div className="flex flex-col p-4 bg-white rounded-lg shadow-md text-neutral-600 dark:text-neutral-300 dark:bg-neutral-800">
              <div className="flex items-center mb-4">
                <span className="flex-1 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                  Ticket code
                </span>
              </div>
              <div className="flex flex-col space-y-4">
                {ticket?.tickets?.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center pb-2 border-b border-neutral-200 dark:border-neutral-700"
                  >
                    <span className="font-normal text-neutral-700 dark:text-neutral-400">
                      {index + 1}. {travelers?.[index]?.name?.firstName}{" "}
                      {travelers?.[index]?.name?.lastName}
                    </span>
                    <span className="font-semibold text-main-900 dark:text-main-300">
                      {item?.documentNumber}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {ticket?.updatedBooking?.payment === "credit" && (
              <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                <span className="flex-1">Corporate Name</span>
                <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
                  {ticket?.updatedBooking?.corporateName}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="pt-5">
          <h3 className="text-2xl font-semibold">Ticket Purchasing Complete</h3>

          <Tab.Group>
            <Tab.List className="flex my-5">
              <Tab as={Fragment}>
                {({ selected }) => (
                  <Link to="#" className="hover:text-gray-400">
                    <button className="p-3 mb-3 ml-5 text-black rounded hover:border">
                      <div className="pb-3 ml-5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-8 h-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z"
                          />
                        </svg>
                      </div>
                      <p className="hover:text-gray-500"> Share Ticket</p>
                    </button>
                  </Link>
                )}
              </Tab>
              <Tab as={Fragment}>
                {({ selected }) => (
                  <>
                    <button
                      className="p-3 mb-3 ml-5 text-black rounded hover:border"
                      onClick={handlePrint}
                      mediaPrint
                      media="print"
                    >
                      <div className="pb-3 ml-14">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-8 h-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                          />
                        </svg>
                      </div>
                      <p className="hover:text-gray-500">Download Ticket pdf</p>
                    </button>

                    <div className="hidden">
                      <AmadeusTicketInfo
                        ref={componentRef}
                        ticketData={ticket}
                        passengerInfo={ticket?.updatedBooking?.passengers}
                        companyInfo={ticket?.updatedBooking?.company}
                      />
                    </div>
                  </>
                )}
              </Tab>
            </Tab.List>
          </Tab.Group>
        </div>
        <div>
          <ButtonPrimary href="/">Explore more flights</ButtonPrimary>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
      <main className="container mt-11 mb-24 lg:mb-32">
        <div className="mx-auto max-w-4xl">{renderContent()}</div>
      </main>
    </div>
  );
};

export default ConfirmTicketAmadeus;
